import { SERVICES_LANDING_PATH } from '@gumtree/shared/src/util/category-brp-entry-cookie';

interface State {
    title: string;
    path: string;
    items: CategoryDataItem[];
    activeId: null | string;
    isOpen: boolean;
}

export interface CategoryDataItemSubItem {
    name: string;
    path: string;
    seoName: string;
}

export interface CategoryDataItem {
    name: string;
    path: string;
    qaAttribute?: string;
    seoName: string;
    subitems?: CategoryDataItemSubItem[];
    subtitle: string;
}

export const initialState: State = {
    activeId: null,
    items: [],
    isOpen: false,
    path: '',
    title: '',
};

export const UPDATE_CATEGORY_NAV = 'UPDATE_CATEGORY_NAV';
export const CATEGORY_NAV_CLOSE = 'CATEGORY_NAV_CLOSE';
export const CATEGORY_NAV_OPEN = 'CATEGORY_NAV_OPEN';

export function redirectCarCategoryToLandingPage(items: CategoryDataItem[]) {
    return items.map((item) => ({
        ...item,
        ...(item.seoName === 'cars-vans-motorbikes' && {
            name: 'Cars & Vehicles',
            path: '/cars-vans-motorbikes/cars',
        }),
    }));
}

export interface CategoryBrpEntryExperiments {
    cars?: boolean;
    services?: boolean;
}

const carsLandingPath = '/cars-vans-motorbikes/cars';

const isSearchPath = (path: string) => path === '/search' || path.startsWith('/search?');

const isServicesBrpSearchPath = (path: string) => {
    if (!isSearchPath(path)) {
        return false;
    }

    const query = path.indexOf('?');
    return (
        query >= 0 &&
        new URLSearchParams(path.slice(query + 1)).get('search_category') === 'business-services'
    );
};

export const isCategoryBrpEntryPath = (path: string) =>
    path === carsLandingPath ||
    path === SERVICES_LANDING_PATH ||
    /^\/cars-vans-motorbikes\/cars\/uk(?:\/|$)/.test(path) ||
    /^\/business-services\/uk(?:\/|$)/.test(path) ||
    isServicesBrpSearchPath(path);

const getLocationSuffix = (path: string, l1SeoName: string, leafSeoName?: string) => {
    const l1Prefix = `/${l1SeoName}`;
    let suffix = path.startsWith(l1Prefix) ? path.slice(l1Prefix.length) : '';

    if (leafSeoName && (suffix === `/${leafSeoName}` || suffix.startsWith(`/${leafSeoName}/`))) {
        suffix = suffix.slice(leafSeoName.length + 1);
    }

    if (!suffix || suffix === '/') {
        return '/uk';
    }

    const ukSegmentIndex = suffix.indexOf('/uk/');
    if (ukSegmentIndex >= 0) {
        return suffix.slice(ukSegmentIndex);
    }

    return suffix.startsWith('/uk') ? suffix : `/uk${suffix}`;
};

const getCarsBrpPath = (path: string) =>
    isSearchPath(path)
        ? path
        : `${carsLandingPath}${getLocationSuffix(path, 'cars-vans-motorbikes', 'cars')}`;

const getServicesBrpPath = (path: string) => {
    if (isSearchPath(path)) {
        return path;
    }

    const locationSuffix = getLocationSuffix(path, 'business-services');
    return locationSuffix === '/uk'
        ? SERVICES_LANDING_PATH
        : `${SERVICES_LANDING_PATH}${locationSuffix}`;
};

export function redirectCategoryLandingPageEntriesToBrp(
    items: CategoryDataItem[],
    { cars = false, services }: CategoryBrpEntryExperiments = {}
) {
    return items.map((item) => {
        if (item.seoName === 'cars-vans-motorbikes') {
            if (!cars) {
                return redirectCarCategoryToLandingPage([item])[0];
            }

            const path = getCarsBrpPath(item.path);
            return {
                ...item,
                name: 'Cars & Vehicles',
                path,
                ...(item.subitems && {
                    subitems: item.subitems.map((subitem) =>
                        subitem.seoName === 'cars' ? { ...subitem, path } : subitem
                    ),
                }),
            };
        }

        if (item.seoName === 'business-services') {
            if (services) {
                return { ...item, path: getServicesBrpPath(item.path) };
            }

            return services === false ? { ...item, path: SERVICES_LANDING_PATH } : item;
        }

        return item;
    });
}

//#region mobile categories
export interface MobileCategoryItem extends CategoryDataItem {
    icon: string;
    gaLabel: string;
}

const mobileSeoNames = {
    'cars-vans-motorbikes': true,
    'for-sale': true,
    'flats-houses': true,
    jobs: true,
    'business-services': true,
};

export function transformMobileCategories(items: CategoryDataItem[]) {
    return items
        .filter(({ seoName }) => mobileSeoNames[seoName])
        .map<MobileCategoryItem>((item) => {
            const seoName = item.seoName as keyof typeof mobileSeoNames;

            switch (seoName) {
                case 'business-services':
                    return { ...item, gaLabel: 'Services', icon: 'icon--spanner' };
                case 'cars-vans-motorbikes':
                    return {
                        ...item,
                        name: 'Cars & Vehicles',
                        path: '/cars-vans-motorbikes/cars',
                        gaLabel: 'Motors',
                        icon: 'icon--car',
                    };
                case 'flats-houses':
                    return {
                        ...item,
                        name: 'Property to Rent',
                        // Preserve location
                        path: item.path.replace('/flats-houses', '/property-to-rent'),
                        gaLabel: 'Property',
                        icon: 'icon--house',
                    };
                case 'for-sale':
                    return { ...item, gaLabel: 'For Sale', icon: 'icon--tag' };
                case 'jobs':
                    return { ...item, gaLabel: 'Jobs', icon: 'icon--tie' };
            }
        });
}
//#endregion

export const updateCategoryNav = (payload) => ({
    type: UPDATE_CATEGORY_NAV,
    payload,
});

export const openCategoryNav = (categoryId, categoryName) => ({
    type: CATEGORY_NAV_OPEN,
    categoryId,
    track: {
        event: 'NavL1CategoryHover',
        eventLabel: categoryName,
    },
});

export const closeCategoryNav = () => ({
    type: CATEGORY_NAV_CLOSE,
});

export default (state = initialState, action): State => {
    switch (action.type) {
        case UPDATE_CATEGORY_NAV:
            return action.payload;
        case CATEGORY_NAV_OPEN:
            return {
                ...state,
                activeId: action.categoryId,
                isOpen: true,
            };
        case CATEGORY_NAV_CLOSE:
            return {
                ...state,
                activeId: null,
                isOpen: false,
            };
        default:
            return state;
    }
};
